import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY } from "./nyHelpers";

// Template87: Día 6 Hudson Yards y Chelsea (Page18). Contenido desde page.
const Template87 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		if (!text) return null;
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const title = page?.texts?.[0]?.content;
	const heroH1 = page?.texts?.[1]?.content;
	const intro = page?.texts?.[2]?.content;
	const vesselH3 = page?.texts?.[3]?.content;
	const vesselP = page?.texts?.[4]?.content;
	const sabiasQueVessel = parseSabiasQueItem(page?.texts?.[5]?.content);
	const shopsH3 = page?.texts?.[6]?.content;
	const shopsP = page?.texts?.[7]?.content;
	const sabiasQueShops = parseSabiasQueItem(page?.texts?.[8]?.content);
	const edgeH3 = page?.texts?.[9]?.content;
	const edgeP = page?.texts?.[10]?.content;
	const sabiasQueEdge = parseSabiasQueItem(page?.texts?.[11]?.content);
	const comoLlegarH4 = page?.texts?.[12]?.content;
	const comoLlegarP = page?.texts?.[13]?.content;
	const yellowEdge = page?.texts?.[14]?.content;

	const hudsonSrc = getImageSrc("hudson");
	const hudsonStickerSrc = getImageSrc("hudson-sticker");
	const vesselSrc = getImageSrc("vessel");
	const edgeSrc = getImageSrc("edge");
	const edgeStickerSrc = getImageSrc("edge-sticker");
	const logoSrc = getCanvasImageUrl("logo");

	const estiloH3 = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 13,
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			{hudsonSrc && (
				<div data-block-id="image-0" style={{ position: "relative" }}>
					<img
						src={hudsonSrc}
						alt="Hudson Yards"
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{hudsonStickerSrc && (
						<img
							src={hudsonStickerSrc}
							alt="Hudson Yards sticker"
							style={{
								width: 90,
								position: "absolute",
								bottom: isSmallScreen ? 90 : 60,
								left: 20,
							}}
						/>
					)}
					{heroH1 && (
						<h1
							style={{
								fontFamily: '"Poppins", sans-serif',
								fontSize: isSmallScreen ? 28 : 32,
								position: "absolute",
								bottom: 0,
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
								margin: 10,
							}}
						>
							{isSmallScreen ? (
								<>
									{heroH1.split(" y ")[0]} y<br />
									{heroH1.split(" y ").slice(1).join(" y ")}
								</>
							) : (
								heroH1
							)}
						</h1>
					)}
				</div>
			)}

			{intro && (
				<div
					data-block-id="text-1"
					style={{
						padding: "10px 12px",
						paddingBottom: 0,
					}}
				>
					<p style={{ ...estiloP, color: "#333" }}>{intro}</p>
				</div>
			)}

			<div
				style={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					gap: 8,
					paddingBottom: isSmallScreen ? 12 : 0,
				}}
			>
				{(vesselH3 || vesselP || vesselSrc) && (
					<div data-block-id="text-2" style={{ display: "flex", gap: 16, position: "relative" }}>
						{vesselSrc && (
							<img
								src={vesselSrc}
								alt="The Vessel"
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<div style={{ width: "60%" }}>
							{vesselH3 && <h3 style={estiloH3}>{vesselH3}</h3>}
							{vesselP && <p style={estiloP}>{vesselP}</p>}
							{sabiasQueVessel && (
								<div style={{ position: "absolute", bottom: -5, left: "32%" }}>
									<SabiasQueNY
										icono={sabiasQueVessel.icono}
										texto={sabiasQueVessel.texto}
									/>
								</div>
							)}
						</div>
					</div>
				)}
				{(shopsH3 || shopsP) && (
					<div
						data-block-id="text-3"
						style={{
							backgroundColor: "#e0f7fa",
							padding: 12,
							borderRadius: 12,
							display: "flex",
							gap: 16,
							alignItems: "center",
							position: "relative",
						}}
					>
						<div style={{ width: "100%" }}>
							{shopsH3 && <h3 style={estiloH3}>{shopsH3}</h3>}
							{shopsP && <p style={estiloP}>{shopsP}</p>}
						</div>
						{sabiasQueShops && (
							<div
								style={{
									position: "absolute",
									bottom: isSmallScreen ? -10 : -20,
									right: "30%",
								}}
							>
								<SabiasQueNY
									icono={sabiasQueShops.icono}
									texto={sabiasQueShops.texto}
								/>
							</div>
						)}
					</div>
				)}
				{(edgeH3 || edgeP || edgeSrc) && (
					<div data-block-id="text-4" style={{ display: "flex", gap: 16 }}>
						{edgeSrc && (
							<img
								src={edgeSrc}
								alt="The Edge"
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<div style={{ width: "60%", position: "relative" }}>
							{edgeH3 && <h3 style={estiloH3}>{edgeH3}</h3>}
							{edgeP && (
								<p
									style={estiloP}
									dangerouslySetInnerHTML={{ __html: edgeP }}
								/>
							)}
							{sabiasQueEdge && (
								<SabiasQueNY
									icono={sabiasQueEdge.icono}
									texto={sabiasQueEdge.texto}
								/>
							)}
						</div>
					</div>
				)}
				{(comoLlegarH4 || comoLlegarP) && (
					<div
						data-block-id="text-5"
						style={{
							backgroundColor: "#e0f7fa",
							border: "3px solid #59c3d8",
							borderRadius: 12,
							padding: "16px 20px",
							fontSize: 12,
							fontFamily: '"Poppins", sans-serif',
							color: "#333",
							position: "relative",
							width: isSmallScreen ? "60%" : "100%",
						}}
					>
						{comoLlegarH4 && (
							<h4
								style={{
									margin: 0,
									marginBottom: 8,
									fontSize: 13,
									fontWeight: 700,
									color: "#007a8d",
									textTransform: "uppercase",
								}}
							>
								{comoLlegarH4}
							</h4>
						)}
						{comoLlegarP && (
							<p
								style={estiloP}
								dangerouslySetInnerHTML={{ __html: comoLlegarP }}
							/>
						)}
						{edgeStickerSrc && (
							<img
								src={edgeStickerSrc}
								alt="Edge"
								style={{
									width: 100,
									position: "absolute",
									bottom: isSmallScreen ? 0 : 10,
									right: isSmallScreen ? -160 : -24,
								}}
							/>
						)}
						{yellowEdge && (
							<div
								style={{
									position: "absolute",
									bottom: isSmallScreen ? 70 : 37,
									right: isSmallScreen ? -140 : 50,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: '"Poppins", sans-serif',
									color: "#444",
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: isSmallScreen ? 160 : 200,
								}}
							>
								{yellowEdge}
							</div>
						)}
					</div>
				)}
			</div>
		</Box>
	);
};

export default Template87;
